// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Badge from "@insite/mobius/Badge";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VideoContainer from "@stanion/App/Components/Media/VideoContainer";
import setSelectedVideoIndex from "@stanion/App/Handlers/ProductDetails/SetSelectedVideoIndex";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import {
    getProductVideos,
    getSelectedVideo,
    getSelectedVideoIndex,
} from "@stanion/App/Store/Selectors/ProductDetailsSelector";
import { getSettingsCollection } from "@stanion/App/Store/Selectors/SettingsSelectors";
import ProductImageCarousel, { ProductImageCarouselStyles } from "@stanion/Overrides/Components/ProductImageCarousel";
import { xScreen } from "@stanion/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    selectedImage: getSelectedImage(state, ownProps.productContext.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
    // XNG-Changes:
    selectedVideo: getSelectedVideo(state, ownProps.productContext.product),
    selectedVideoIndex: getSelectedVideoIndex(state, ownProps.productContext.product),
    videos: getProductVideos(state, ownProps.productContext.product),
    catalogSettings: getSettingsCollection(state).catalogGeneralSettings,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
    // XNG-Changes:
    setSelectedVideoIndex,
};

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes:
    imageContainer?: InjectableCss;
    modalImageContainer?: InjectableCss;
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        centeringWrapper: {
            css: css`
                width: 100%;
                max-height: 78vh;
                max-width: 100%;
            `,
        },
        img: {
            width: "2000px",
            height: "2000px",
            css: css`
                margin: auto;
                width: auto;
                height: auto;
                max-height: 100%;
                max-width: 100%;
                ${xScreen("lte", "md")} {
                    width: auto;
                    height: auto;
                }
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    // XNG-Changes:
    imageCarousel: {
        carouselSlide: {
            css: css`
                flex: 0 0 ${100 / 6}%;
            `,
        },
    },
    modalImageContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            min-height: 400px;
            min-width: 400px;
            /* XNGAGE Changes */
            max-width: 100%;
            ${xScreen("lte", "sm")} {
                min-width: 100%;
            }
        `,
    },
    imageContainer: {
        css: css`
            height: 317px;
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            background-color: ${getColor("primary.contrast")};
            overflow: hidden;
            &&&& img,
            video,
            iframe {
                cursor: pointer;
                max-height: 317px;
            }

            &&&& video,
            iframe {
                min-height: 317px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            && {
                                max-width: 500px;
                                width: 100%;
                                margin: auto;
                            }
                        `,
                        null,
                        css`
                            height: 317px;
                            &&&& img,
                            video,
                            iframe {
                                max-height: 317px;
                            }
                            &&& video,
                            iframe {
                                min-height: 317px;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedVideo,
    selectedImageIndex,
    selectedVideoIndex,
    setSelectedImageIndex,
    setSelectedVideoIndex,
    catalogSettings,
    videos,
}: Props) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);

    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        // XNG-Changes: unset video index
        if (videos && videos.length > 0) {
            setSelectedVideoIndex({ index: -1, product });
        }
        setSelectedImageIndex({ index });
    };

    // XNG-Changes: add video handler
    const selectVideoHandler = (index: number) => {
        setSelectedImageIndex({ index: -1 });
        setSelectedVideoIndex({ index, product });
    };

    const { product } = productContext;

    if (!product) {
        return null;
    }
    // XNg-Changes: put not found image if no image presented
    const { notFoundLargeImagePath, notFoundMediumImagePath, notFoundSmallImagePath } = catalogSettings;

    const video = selectedVideo;

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath || notFoundLargeImagePath,
            mediumImagePath: product.mediumImagePath || notFoundMediumImagePath,
            smallImagePath: product.smallImagePath || notFoundSmallImagePath,
        } as ImageModel);

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
        styles,
    });

    // XNG-Changes: restructure widget to add videos
    const videoSection = video && (
        <VideoContainer
            src={video.link as string}
            isYoutubeVideo={video.isYoutubeVideo}
            extension={getFileExtension(video.link as string)}
            controls={true}
            insideModal={fullPrimaryImageModalIsOpen}
        ></VideoContainer>
    );

    const path = image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }
    return (
        <>
            <StyledWrapper {...styles.imageContainer}>
                {videoSection}
                {!video && !showOverlayBadge && (
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        onClick={imageClickHandler}
                        extendedStyles={styles.primaryImage}
                    />
                )}
                {showOverlayBadge && overlayBadge && (
                    <StyledWrapper {...styles.badgeAreaWrapper} ref={badgeAreaWrapperRef}>
                        <ProductPrimaryImage
                            product={product}
                            image={image}
                            onClick={imageClickHandler}
                            extendedStyles={styles.primaryImage}
                        />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>

            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <StyledWrapper {...styles.modalImageContainer}>
                    {videoSection}
                    {!video && (
                        <ProductPrimaryImage
                            product={product}
                            image={image}
                            useLargeImage={true}
                            extendedStyles={styles.fullPrimaryImage}
                        />
                    )}
                </StyledWrapper>
                {product.images && product.images.length > 1 && (
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        selectedVideoIndex={selectedVideoIndex}
                        onSelectImage={selectImageHandler}
                        onSelectVideo={selectVideoHandler}
                        extendedStyles={styles.imageCarousel}
                    />
                )}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
